import "devextreme/dist/css/dx.light.css";
import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import vuetify from "./plugins/vuetify";

import axios from "axios";
import VueAxios from "vue-axios";

import ElementUI from "element-ui";
import { Button, Input, Form, Alert, FormItem } from "element-ui";
import "element-ui/lib/theme-chalk/index.css";

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import "./assets/css/inter.css";
import "./customstyles.css";
import VueCodeHighlight from "vue-code-highlight";
import * as Sentry from "@sentry/vue";

import { createPinia, PiniaVuePlugin } from "pinia";

import configService from "./services/config";
import launchDarklyService from "./services/launchdarkly";

Vue.use(PiniaVuePlugin);
const pinia = createPinia();

import loaderPlugin from "./plugins/loaderPlugin.js";
Vue.use(loaderPlugin, { store });

// Import and use LaunchDarkly plugin
import LaunchDarklyPlugin from "./plugins/launchdarkly.js";
Vue.use(LaunchDarklyPlugin);

// Import and use Notification plugin
import notificationPlugin from "./plugins/notificationPlugin.js";
Vue.use(notificationPlugin);

// Register Web Components
import "../../components/webcomponents-dist/exiq-webcomponents.es.js";

// Tell Vue to ignore custom elements starting with exiq-
Vue.config.ignoredElements = [/exiq-\w*/];

Vue.config.productionTip = false;

Vue.use(ElementUI);
Vue.component(Button.name, Button);
Vue.component(Input.name, Input);
Vue.component(Form.name, Form);
Vue.component(Alert.name, Alert);
Vue.component(FormItem.name, FormItem);
Vue.use(VueAxios, axios);
Vue.use(VueCodeHighlight);

const options = {
  position: "top-right",
  timeout: 3000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: false,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: true,
  closeButton: "button",
  icon: true,
};
Vue.use(Toast, options);

// ============================================
// Chunk Loading Error Handler
// ============================================
// Handles errors when webpack chunks fail to load (e.g., after deployments)
// This prevents the "SyntaxError: Unexpected token '<'" error when chunks are outdated

const CHUNK_LOAD_ERROR_RETRY_KEY = "chunkLoadErrorRetry";
const MAX_CHUNK_RETRY_COUNT = 1;

// Global error handler for chunk loading failures
window.addEventListener(
  "error",
  (event) => {
    const { message, filename } = event;

    // Check if this is a chunk loading error
    const isChunkLoadError =
      message?.includes("Loading chunk") ||
      message?.includes("ChunkLoadError") ||
      (filename &&
        /\.js$/.test(filename) &&
        message?.includes("Unexpected token"));

    if (isChunkLoadError) {
      const retryCount = parseInt(
        sessionStorage.getItem(CHUNK_LOAD_ERROR_RETRY_KEY) || "0"
      );

      if (retryCount < MAX_CHUNK_RETRY_COUNT) {
        console.warn("Chunk loading failed, attempting reload...", {
          message,
          filename,
          retryCount,
        });
        sessionStorage.setItem(
          CHUNK_LOAD_ERROR_RETRY_KEY,
          String(retryCount + 1)
        );

        // Reload the page to get fresh chunks
        window.location.reload();
        event.preventDefault();
      } else {
        console.error("Chunk loading failed after retries", {
          message,
          filename,
          retryCount,
        });
        sessionStorage.removeItem(CHUNK_LOAD_ERROR_RETRY_KEY);

        // Let Sentry capture this error
        if (window.Sentry) {
          window.Sentry.captureException(
            new Error(`Chunk loading failed: ${message}`),
            {
              tags: { chunk_error: true },
              extra: { filename, retryCount },
            }
          );
        }
      }
    }
  },
  true
);

// Clear retry counter on successful load
window.addEventListener("load", () => {
  sessionStorage.removeItem(CHUNK_LOAD_ERROR_RETRY_KEY);
});

// ============================================
// Router Chunk Loading Error Handler
// ============================================
// Handle lazy-loaded route component failures
router.onError((error) => {
  if (error.name === "ChunkLoadError" || /Loading chunk/.test(error.message)) {
    const retryCount = parseInt(
      sessionStorage.getItem(CHUNK_LOAD_ERROR_RETRY_KEY) || "0"
    );

    if (retryCount < MAX_CHUNK_RETRY_COUNT) {
      console.warn("Route chunk loading failed, attempting reload...", error);
      sessionStorage.setItem(
        CHUNK_LOAD_ERROR_RETRY_KEY,
        String(retryCount + 1)
      );
      window.location.reload();
    } else {
      console.error("Route chunk loading failed after retries", error);
      sessionStorage.removeItem(CHUNK_LOAD_ERROR_RETRY_KEY);
    }
  }
});

async function initializeApp() {
  try {
    await configService.load();
    Vue.prototype.$config = configService;

    // Check for maintenance mode
    if (configService.get("maintenanceMode")) {
      store.commit("isLoading/setMaintenanceMode", true);
    }

    axios.defaults.baseURL = configService.get("baseURL");

    // Load secure config from localStorage if user is already authenticated
    const token = localStorage.getItem("token");
    const currentAccountID = localStorage.getItem("currentAccountID");
    if (token && currentAccountID) {
      configService.loadSecureConfigFromStorage();
    }

    const DSN_URL = configService.get("dsnUrl");
    const ENVIRONMENT = configService.get("environment");
    const RELEASE = configService.get("sentryRelease");
    if (DSN_URL) {
      Sentry.init({
        Vue,
        dsn: DSN_URL,
        sendDefaultPii: true,
        environment: ENVIRONMENT,
        release: RELEASE,
        integrations: [Sentry.browserTracingIntegration({ router })],
        tracesSampleRate: 1.0,
        beforeSend(event) {
          return event;
        },
      });
    }

    await launchDarklyService.initialize();
    try {
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");
      const accountId = localStorage.getItem("currentAccountID");
      const accessPermissions = localStorage.getItem("accessPermission");

      if (token && userStr && accountId && accessPermissions) {
        const user = JSON.parse(userStr);
        const permissions = JSON.parse(accessPermissions);
        const account = {
          id: parseInt(accountId),
          name: permissions.account_name || `Account ${accountId}`,
          owner_email: permissions.owner_email || user?.email,
        };

        await launchDarklyService.identifyTenant(user, account);
      }
    } catch (e) {
      console.warn("Could not identify LaunchDarkly context on startup:", e);
    }
  } catch (error) {
    console.error("Application initialization failed:", error);
  }

  new Vue({
    router,
    store,
    vuetify,
    pinia,
    render: (h) => h(App),
  }).$mount("#app");
}

initializeApp();
